import { GitProvenanceResponse } from "common";
import { JSX, ReactNode } from "react";

/**
 * The Provenance tab's body (git-4-2), shared by the process editor and the pipeline page: where
 * this revision came from and what was published from it. Read only, and it never shows a
 * credential: the server sends only the credential's kind, and this renders only that.
 */

type Props = {
  subject: "process" | "pipeline";
  data: GitProvenanceResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  /** The read answers 403 to anyone who may not write the revision. That is an answer, not a failure. */
  isForbidden: boolean;
};

const formatDate = (iso: string | null): string =>
  null === iso ? "Unknown" : new Date(iso).toLocaleString();

const Row = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): JSX.Element => (
  <div className="grid grid-cols-1 gap-1 py-1 sm:grid-cols-4">
    <dt className="text-xs text-gray-500">{label}</dt>
    <dd className="text-sm break-all text-gray-800 sm:col-span-3">
      {children}
    </dd>
  </div>
);

const Mono = ({ children }: { children: ReactNode }): JSX.Element => (
  <span className="font-mono text-xs">{children}</span>
);

const Section = ({
  title,
  testId,
  children,
}: {
  title: string;
  testId: string;
  children: ReactNode;
}): JSX.Element => (
  <section
    className="rounded-md border border-gray-200 p-3"
    data-testid={testId}>
    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    <div className="mt-2">{children}</div>
  </section>
);

const ORIGIN_KIND_LABEL: Record<string, string> = {
  git: "Imported from a git repository",
  instance: "Imported from a Foundry bundle (.vf)",
  manual: "Recorded by hand",
};

export default function GitProvenancePanel({
  subject,
  data,
  isLoading,
  isError,
  isForbidden,
}: Props): JSX.Element {
  if (isLoading) {
    return (
      <p className="p-4 text-sm text-gray-500">Loading the provenance...</p>
    );
  }
  if (isError || undefined === data) {
    return (
      <p className="p-4 text-sm text-red-600" data-testid="provenanceError">
        {isForbidden
          ? `You need write permission on this ${subject} to see its provenance.`
          : "The provenance could not be read. Reload the page and try again."}
      </p>
    );
  }

  const { connection, origin, publications } = data;

  return (
    <div className="space-y-4 p-4">
      <p className="text-sm text-gray-500">
        A read only record of where revision {data.revId} of this {subject} came
        from and where it publishes. Stored credentials are named by kind only.
      </p>

      <Section title="Repository connection" testId="provenanceConnection">
        {null === connection ? (
          <p className="text-sm text-gray-500">
            This {subject} is not connected to a repository.
          </p>
        ) : (
          <dl>
            <Row label="Repository">
              <Mono>{connection.repoUrl}</Mono>
            </Row>
            <Row label="Folder">
              <Mono>{connection.repoPath}</Mono>
            </Row>
            <Row label="Provider">{connection.provider}</Row>
            <Row label="Branch">
              <Mono>{connection.defaultBranch}</Mono>
            </Row>
            <Row label="Credential">
              {null === connection.credentialKind
                ? "None (the repository is read without signing in)"
                : `A ${connection.credentialKind} credential is stored`}
            </Row>
            {"process" === subject ? (
              <Row label="Container registry">
                {connection.hasRegistryCredential
                  ? "A registry login is stored"
                  : "No registry login"}
              </Row>
            ) : null}
          </dl>
        )}
      </Section>

      <Section title="Origin" testId="provenanceOrigin">
        {null === origin ? (
          <p className="text-sm text-gray-500">
            This revision was written here. It was not imported from a
            repository.
          </p>
        ) : (
          <dl>
            <Row label="Source">
              {ORIGIN_KIND_LABEL[origin.sourceKind] ?? origin.sourceKind}
            </Row>
            {null !== origin.repoUrl ? (
              <Row label="Repository">
                <Mono>{origin.repoUrl}</Mono>
              </Row>
            ) : null}
            {null !== origin.repoPath ? (
              <Row label="Folder">
                <Mono>{origin.repoPath}</Mono>
              </Row>
            ) : null}
            {"git" === origin.sourceKind ? (
              <Row label="Tag">
                {null === origin.tag ? (
                  <span className="text-gray-500">Not recorded</span>
                ) : (
                  <Mono>{origin.tag}</Mono>
                )}
              </Row>
            ) : null}
            {null !== origin.commitId ? (
              <Row
                label={
                  null === origin.commitAlgo
                    ? "Commit"
                    : `Commit (${origin.commitAlgo})`
                }>
                <Mono>{origin.commitId}</Mono>
              </Row>
            ) : null}
            <Row label="Content digest">
              <Mono>{origin.contentHash}</Mono>
            </Row>
            <Row label="Imported by">{origin.importedBy ?? "Unknown"}</Row>
            <Row label="Imported at">{formatDate(origin.importedAt)}</Row>
          </dl>
        )}
      </Section>

      {"process" === subject ? (
        <Section title="Publications" testId="provenancePublications">
          {0 === publications.length ? (
            <p className="text-sm text-gray-500">
              No gated publication of this revision is recorded. Container
              digest and platform appear here once one is.
            </p>
          ) : (
            <div className="space-y-3">
              {publications.map(publication => (
                <dl
                  key={publication.commitId}
                  className="rounded bg-gray-50 p-2"
                  data-testid="provenancePublication">
                  <Row label="Tag">
                    <Mono>{publication.tag}</Mono>
                  </Row>
                  <Row label={`Commit (${publication.commitAlgo})`}>
                    <Mono>{publication.commitId}</Mono>
                  </Row>
                  <Row label="Tree">
                    <Mono>{publication.treeId}</Mono>
                  </Row>
                  <Row label="Content digest">
                    <Mono>{publication.contentHash}</Mono>
                  </Row>
                  <Row label="Container">
                    <Mono>{publication.containerRef}</Mono>
                  </Row>
                  <Row label="Container digest">
                    <Mono>{publication.containerDigest}</Mono>
                  </Row>
                  <Row label="Platform">
                    {publication.imagePlatform ?? "Unknown"}
                  </Row>
                  <Row label="Versions">
                    generator {publication.generatorVersion}, layout{" "}
                    {publication.layoutVersion}
                  </Row>
                  <Row label="Published by">
                    {publication.publishedBy ?? "Unknown"}
                  </Row>
                  <Row label="Published at">
                    {formatDate(publication.publishedAt)}
                  </Row>
                </dl>
              ))}
            </div>
          )}
        </Section>
      ) : null}
    </div>
  );
}
